import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AUTOCOMPLETEFROMENUMERATION_COLUMN_URI, AutocompleteFromUri, AUTOCOMPLETEFROMURI_COLUMN_URI, AutocompleteOption, AutocompleteWithGivenOptions, AUTOCOMPLETEWITHGIVENOPTIONS_COLUMN_URI, AUTOCOMPLETE_FROM_MASTER_DATA, BooleanValue, CHECKBOX_COLUMN_URI, CODEBARSCANNERWITHSELECT_URI, CODEBAR_SCANNER_URI, ConditionPath, DATEPICKER_COLUMN_URI, DATETIMEPICKER_COLUMN_URI, DType, DTYPEAUTOCOMPLETE_URI, DTYPEBOOLEAN_URI, DTYPECHIPLIST_URI, DTypeCol, DTYPECUSTOM_URI, DTYPEDATETIME_URI, DTYPEDECORATION_URI, DTYPEEMBEDDED_URI, DTypeFamilies, DTypeFamily, DTYPEFILE_URI, DTYPENUMBER_URI, DTYPESELECT_URI, DTYPETEXT_URI, DTYPETRIGGER_URI, FORMATTEDTEXT_COLUMN_URI, INTEGER_COLUMN_URI, LANGUAGETEXTAREA_COLUMN_URI, LANGUAGETEXT_COLUMN_URI, NextLocator, Operator, RADIO_COLUMN_URI, RATING_COLUMN_URI, REAL_COLUMN_URI, SECTION_COLUMN_URI, SELECTFACADE_COLUMN_URI, SELECTFROMCONCEPTSERVICE_COLUMN_URI, SELECTFROMENUMERATION_COLUMN_URI, SELECTFROMMASTERDATA_COLUMN_URI, SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI, SelectFromUri, SELECTFROMURI_COLUMN_URI, SELECTMASTER_COLUMN_URI, SELECTNEXTTASKTYPE_COLUMN_URI, SelectOption, SELECTSERVICEUSERASSIGNMENT_COLUMN_URI, SELECTSERVICE_COLUMN_URI, SelectWithGivenOptions, SELECTWITHGIVENOPTIONS_COLUMN_URI, SUBSECTION_COLUMN_URI, TEXTAREA_COLUMN_URI, TEXT_COLUMN_URI, TIMEPICKER_COLUMN_URI, TIMERANGESCONVERTER_COLUMN_URI, TRIGGER_COLUMN_URI, TRISTATE_COLUMN_URI, SELECTFROMRESOURCESCANNER_URI } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { formatISO } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { debounceTime, firstValueFrom, map, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { DTypeService } from '../../dtype.service';
import { SearchCompanyUserMissionUserListDNRequest } from '../../dtype.types';
import { PlanningArea } from '../../../../modules/admin/services/services.types';
import { CompanyUser } from '../../../user/companyUser.model';
import { NextTaskType } from '../../../task-type/task-type.types';
import { DialogsService } from '../../../../dialogs/dialogs.service';
import { FuseConfirmationService } from '../../../../../@fuse/services/confirmation/confirmation.service';
import { LocalizePipe } from '../../../pipes/localize.pipe';
import { ServicesService } from '../../../../modules/admin/services/services.service';
import { TaskTypeService } from '../../../task-type/task-type.service';
import { Utils } from '../../../utils/utils';

@Component({
    selector: 'dtype-condition',
    templateUrl: './dtype-condition.dialog.html',
    styles: [
        /* language=SCSS */
        `
            .dtype-condition-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DtypeConditionDialogComponent implements OnInit, OnDestroy {

    readonly checkboxUri = CHECKBOX_COLUMN_URI;
    readonly tristateUri = TRISTATE_COLUMN_URI;
    readonly radioUri = RADIO_COLUMN_URI;
    readonly datepickerUri = DATEPICKER_COLUMN_URI;
    readonly timepickerUri = TIMEPICKER_COLUMN_URI;
    readonly datetimepickerUri = DATETIMEPICKER_COLUMN_URI;
    readonly timerangesUri = TIMERANGESCONVERTER_COLUMN_URI;
    readonly separatorKeysCodes: number[] = [ENTER, COMMA];

    formGroup = new FormGroup({
        field: new FormControl('', Validators.required),
        exportField: new FormControl(''),
        fieldType: new FormControl<string>('', Validators.required),
        operator: new FormControl<Operator>(undefined, Validators.required),
        operand: new FormControl(),
        path: new FormControl(),
        isReadOnly: new FormControl<boolean>(false)
    });

    chiplistControl = new FormControl<string>('');
    options: SelectOption[];
    filteredOptions: SelectOption[];
    searchUserControl = new FormControl<string | CompanyUser>('');
    filteredUsers: CompanyUser[] = [];
    usersMap = new Map<string, string>();
    planningAreaList: PlanningArea[];
    taskTypeMap: Map<string, NextTaskType[]>;
    exportFields: string[] = [];
    selectMasterDType: DType;
    currentExportField: DTypeCol;

    fields: DTypeCol[];
    currentField: DTypeCol;

    operators: SelectOption[];
    families: DTypeFamily[] = [];
    commonConditions = [
        Operator.EMPTY, Operator.NOT_EMPTY,
        Operator.HIDDEN, Operator.NOT_HIDDEN,
        Operator.MANDATORY, Operator.NOT_MANDATORY,
        Operator.READONLY, Operator.NOT_READONLY
    ];
    conditionsByComponent = new Map<string, Map<string, Operator[]>>();

    private unsubscribeAll = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            field: DTypeCol;
            fields: DTypeCol[];
            operatorsOptions: SelectOption[];
            condition: ConditionPath;
            conditions: ConditionPath[];
            dtype: DType;
            locator: NextLocator;
        },
        private dialogsService: DialogsService,
        private dtypeService: DTypeService,
        private _fuseConfirmationService: FuseConfirmationService,
        private localize: LocalizePipe,
        public matDialogRef: MatDialogRef<DtypeConditionDialogComponent>,
        private servicesService: ServicesService,
        private taskTypeService: TaskTypeService,
        private translocoService: TranslocoService,
    ) {
        if (this.data.locator.serviceId && this.data.dtype.uri.startsWith('form://')) {
            this.fields = this.data.fields.filter(col => col.name !== this.data.field.name && (col.type !== TRIGGER_COLUMN_URI || col.name === '_userId' || col.name === '_planningAreaId' || col.name === '_taskTypeUri')
                && col.type !== FORMATTEDTEXT_COLUMN_URI && col.type !== SECTION_COLUMN_URI && col.type !== SUBSECTION_COLUMN_URI);
        } else if (this.data.dtype.uri.startsWith('form://')) {
            this.fields = this.data.fields.filter(col => col.name !== this.data.field.name && (col.type !== TRIGGER_COLUMN_URI || col.name === '_taskTypeUri')
                && col.type !== FORMATTEDTEXT_COLUMN_URI && col.type !== SECTION_COLUMN_URI && col.type !== SUBSECTION_COLUMN_URI);
        } else {
            this.fields = this.data.fields.filter(col => col.name !== this.data.field.name && col.type !== TRIGGER_COLUMN_URI && col.type !== FORMATTEDTEXT_COLUMN_URI
                && col.type !== SECTION_COLUMN_URI && col.type !== SUBSECTION_COLUMN_URI);
        }

        this.formGroup.get('field').valueChanges.pipe(
            takeUntil(this.unsubscribeAll)
        ).subscribe(async (field) => {
            this.onChangeField(field);
            if (this.currentField.type === SELECTFROMURI_COLUMN_URI) {
                const selectField = (this.currentField as SelectFromUri);
                if (this.selectMasterDType?.uri !== selectField.uri) {
                    this.selectMasterDType = await this.dtypeService.getDatatype({ retrieveDTypeBody: { uri: selectField.uri, bindableOptions: true } });
                }
            }
        });

        this.formGroup.get('exportField').valueChanges.pipe(
            takeUntil(this.unsubscribeAll)
        ).subscribe((exportField) => {
            this.onChangeExportField(exportField);
        });

        this.formGroup.get('operator').valueChanges.pipe(
            takeUntil(this.unsubscribeAll)
        ).subscribe((operator) => {
            this.onChangeOperator(operator);
        });

        this.chiplistControl.valueChanges
            .pipe(
                takeUntil(this.unsubscribeAll),
                startWith(null),
                map((value: string | null) => {
                    if (this.currentFieldIsSelect()) {
                        this.options = (this.currentField as SelectWithGivenOptions).option ?? ((this.currentField as SelectFromUri).uri ? this.data.dtype.link?.[(this.currentField as SelectFromUri).uri] : [])
                        return value ? this.filterOptions(value) : [...this.options];
                    } else if (this.currentFieldIsAutocomplete()) {
                        this.options = (this.currentField as AutocompleteWithGivenOptions).option ?? ((this.currentField as AutocompleteFromUri).uri ? this.data.dtype.link?.[(this.currentField as SelectFromUri).uri] : []);
                        return value ? this.filterOptions(value) : [...this.options];
                    }
                })
            ).subscribe(options => this.filteredOptions = options ?? []);

        this.searchUserControl.valueChanges
                .pipe(
                    takeUntil(this.unsubscribeAll),
                    startWith(''),
                    debounceTime(500),
                    switchMap(value => {
                        if (value && typeof value === 'string') {
                            const request: SearchCompanyUserMissionUserListDNRequest = {
                                locator: this.data.locator,
                                expr: value,
                            };
                            return this.dtypeService.searchCompanyUserMissionUserListDN(request);
                        } else {
                            const users: CompanyUser[] = [];
                            return of(users);
                        }
                    })
                ).subscribe(users => this.filteredUsers = users);
    }

    get field() {
        return this.formGroup.get('field').value;
    }

    get exportField() {
        return this.formGroup.get('exportField').value;
    }

    get fieldType() {
        return this.formGroup.get('fieldType').value;
    }

    get operator() {
        return this.formGroup.get('operator').value;
    }

    get operand() {
        return this.formGroup.get('operand').value;
    }

    get isReadOnly() {
        return this.formGroup.get('isReadOnly').value;
    }

    set field(field: string) {
        this.formGroup.get('field').setValue(field);
    }

    set exportField(exportField: string) {
        this.formGroup.get('exportField').setValue(exportField);
    }

    set fieldType(fieldType: string) {
        this.formGroup.get('fieldType').setValue(fieldType);
    }

    set operator(operator: Operator) {
        this.formGroup.get('operator').setValue(operator);
    }

    set operand(operand: any) {
        this.formGroup.get('operand').setValue(operand);
    }

    set isReadOnly(isReadOnly: boolean) {
        this.formGroup.get('isReadOnly').setValue(isReadOnly);
    }

    ngOnInit() {
        this.dtypeService.retrieveDTypeBuilderCollection().subscribe(async (families) => {
            this.fillConditionsMap(families);
            if (this.data.condition) {
                this.field = this.data.condition.field.includes('.') ? this.data.condition.field.substring(0, this.data.condition.field.indexOf('.')) : this.data.condition.field;
                if (this.currentField.type === SELECTFROMURI_COLUMN_URI) {
                    const selectField = (this.currentField as SelectFromUri);
                    if (this.selectMasterDType?.uri !== selectField.uri) {
                        this.selectMasterDType = await this.dtypeService.getDatatype({ retrieveDTypeBody: { uri: selectField.uri, bindableOptions: true } });
                    }
                }
                this.exportField = this.data.condition.field.includes('.') ? this.data.condition.field.substring(this.data.condition.field.lastIndexOf('.') + 1) : '';
                // this.fieldType = this.data.condition.fieldType;
                this.operator = this.data.condition.operator;
                this.operand = this.fieldType === this.checkboxUri ? this.data.condition.operand === BooleanValue.TRUE ? true : false : this.data.condition.operand;
                this.isReadOnly = this.data.condition.isReadOnly;
                this.formGroup.get('path').setValue(this.data.condition.path);
                if (this.currentField.name === '_userId' && this.operand?.length > 0) {
                    this.dtypeService.retrieveCompanyUserMissionUserList(this.operand).subscribe(users => {
                        for (const user of users) {
                            if (!this.usersMap.has(user.userId)) {
                                this.usersMap.set(user.userId, user.displayName);
                            }
                        }
                    });
                }
                if (this.isReadOnly) {
                    this.formGroup.disable();
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    removeUser(index: number) {
        const value = this.formGroup.get('operand')?.value ?? [];
        if (value[index] !== undefined) {
            value.splice(index, 1);
            this.operand = value;
        }
    }

    addUser(event: MatChipInputEvent) {
        const value = (event.value || '').trim();
        if (value) {
            const option = this.filteredUsers.find(o => Utils.removeAccents(o.userId).toLowerCase() === Utils.removeAccents(value).toLowerCase());
            if (option) {
                const selectedOptions = this.formGroup.get('operand')?.value ?? [];
                if (selectedOptions.findIndex(opt => opt === option.userId) === -1) {
                    selectedOptions.push(option);
                    this.operand = selectedOptions;
                }
            }
        }

        event.chipInput.clear();
        this.searchUserControl.setValue('');
    }

    selectedUser(event: MatAutocompleteSelectedEvent) {
        const selectedOptions = this.formGroup.get('operand')?.value ?? [];
        if (selectedOptions.findIndex(opt => opt === event.option.value) === -1) {
            selectedOptions.push(event.option.value);
            this.operand = selectedOptions;
            if (!this.usersMap.has(event.option.value)) {
                const user = this.filteredUsers.find(u => u.userId === event.option.value);
                if (user) {
                    this.usersMap.set(event.option.value, user.displayName);
                }
            }
        }
        this.searchUserControl.setValue('');
    }

    async save() {
        const confirmDialog = this.openConfirmDialog(this.translocoService.translate('saveConditionConfirm'));
        const confirm = await firstValueFrom(confirmDialog.afterClosed());
        if (confirm === 'confirmed') {
            this.formGroup.markAllAsTouched();
            if (this.formGroup.valid) {
                const condition: ConditionPath = this.formGroup.getRawValue();
                const conditionField = this.exportField ? `${condition.field}.export.${this.exportField}` : condition.field;
                const conditionOperand = this.fieldType === this.checkboxUri ? condition.operand ? BooleanValue.TRUE : BooleanValue.FALSE : condition.operand;
                if (this.data.conditions.find(cond => cond.path !== condition.path && cond.field === conditionField
                    && cond.operand === conditionOperand && cond.operator === condition.operator)) {
                        this.dialogsService.openErrorDialog({
                            message: this.translocoService.translate('conditionAlreadyExists')
                        });
                    return;
                }
                condition.field = conditionField;
                condition.operand = conditionOperand;
                this.matDialogRef.close(condition);
            } else {
                this.dialogsService.openErrorDialog({
                    message: this.translocoService.translate('errorsInForm')
                });
            }
        }
    }

    addToChiplist(event: MatChipInputEvent) {
        const value = event.value || '';
        const list: string[] = this.operand ?? [];
        list.push(value);
        this.operand = list;
        this.chiplistControl.setValue('');
    }

    removeFromChiplist(index: number) {
        const list: string[] = this.operand;
        list.splice(index, 1);
        this.operand = list;
    }

    selectedAutocomplete(event: MatAutocompleteSelectedEvent) {
        const value = cloneDeep(event.option.value);
        if (value) {
            const list: string[] = this.operand ?? [];
            list.push(value);
            this.operand = list;
        }
        this.chiplistControl.setValue(undefined);
    }

    getChipCaption(chip: string) {
        const options = this.options;
        const option = options.find(opt => opt.code === chip);
        if (option) {
            return this.localize.transform(option.value);
        }
        return '';
    }

    currentFieldIsText() { // TODO languagetext no se puede hacer equals
        return [
            TEXT_COLUMN_URI, TEXTAREA_COLUMN_URI,
            LANGUAGETEXT_COLUMN_URI, LANGUAGETEXTAREA_COLUMN_URI, CODEBAR_SCANNER_URI
        ].includes(this.fieldType);
    }

    currentFieldIsNumber() {
        return [INTEGER_COLUMN_URI, REAL_COLUMN_URI, RATING_COLUMN_URI].includes(this.fieldType);
    }

    currentFieldIsSelect() {
        return [
            SELECTFACADE_COLUMN_URI, SELECTFROMCONCEPTSERVICE_COLUMN_URI, SELECTFROMENUMERATION_COLUMN_URI, SELECTFROMRESOURCESCANNER_URI,
            SELECTFROMURI_COLUMN_URI, SELECTMASTER_COLUMN_URI, SELECTWITHGIVENOPTIONS_COLUMN_URI,
            SELECTFROMMASTERDATA_COLUMN_URI, SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI, SELECTNEXTTASKTYPE_COLUMN_URI, CODEBARSCANNERWITHSELECT_URI,
            SELECTSERVICE_COLUMN_URI, SELECTSERVICEUSERASSIGNMENT_COLUMN_URI
        ].includes(this.fieldType);
    }

    fieldIsSelect(fieldType: string) {
        return [
            SELECTFACADE_COLUMN_URI, SELECTFROMCONCEPTSERVICE_COLUMN_URI, SELECTFROMENUMERATION_COLUMN_URI, SELECTFROMRESOURCESCANNER_URI,
            SELECTFROMURI_COLUMN_URI, SELECTMASTER_COLUMN_URI, SELECTWITHGIVENOPTIONS_COLUMN_URI,
            SELECTFROMMASTERDATA_COLUMN_URI, SELECTFROMNEXTSERVICIOPLANNINGAREA_COLUMN_URI, SELECTNEXTTASKTYPE_COLUMN_URI, CODEBARSCANNERWITHSELECT_URI,
            SELECTSERVICE_COLUMN_URI, SELECTSERVICEUSERASSIGNMENT_COLUMN_URI
        ].includes(fieldType);
    }

    currentFieldIsAutocomplete() {
        return [
            AUTOCOMPLETEFROMENUMERATION_COLUMN_URI, AUTOCOMPLETEFROMURI_COLUMN_URI,
            AUTOCOMPLETEWITHGIVENOPTIONS_COLUMN_URI, AUTOCOMPLETE_FROM_MASTER_DATA
        ].includes(this.fieldType);
    }

    fieldIsAutocomplete(fieldType: string) {
        return [
            AUTOCOMPLETEFROMENUMERATION_COLUMN_URI, AUTOCOMPLETEFROMURI_COLUMN_URI,
            AUTOCOMPLETEWITHGIVENOPTIONS_COLUMN_URI, AUTOCOMPLETE_FROM_MASTER_DATA
        ].includes(fieldType);
    }

    currentFieldIsTrigger() {
        return TRIGGER_COLUMN_URI === this.fieldType;
    }

    openDatepickerOnClick(datepicker: MatDatepicker<Date>) {
        if (!datepicker.opened) {
            datepicker.open();
        }
    }

    dateChange(event: any) {
        this.operand = event.value ? formatISO(Date.parse(String(event.value)), { representation: 'date' }) : '';
    }

    private fillConditionsMap(families: DTypeFamilies) {
        for (const [uri, family] of Object.entries(families)) {
            for (const component of family.component) {
                let conditions: Operator[] = [];
                if (uri === DTYPETRIGGER_URI) {
                    conditions.push(Operator.ONE_OF, Operator.NOT_ONE_OF);
                } else {
                    conditions = [...this.commonConditions];
                    switch (uri) {
                        case DTYPEAUTOCOMPLETE_URI:
                            conditions.push(Operator.EQUALS, Operator.NOT_EQUALS, Operator.ONE_OF, Operator.NOT_ONE_OF);
                            break;
                        case DTYPEBOOLEAN_URI:
                            conditions.push(Operator.EQUALS, Operator.NOT_EQUALS);
                            break;
                        case DTYPECHIPLIST_URI:
                            break;
                        case DTYPECUSTOM_URI:
                            break;
                        case DTYPEDATETIME_URI:
                            switch (component.uri) {
                                case DATEPICKER_COLUMN_URI:
                                case TIMEPICKER_COLUMN_URI:
                                    conditions.push(Operator.EQUALS, Operator.NOT_EQUALS, Operator.GREATERTHAN, Operator.LESSTHAN);
                                    break;
                            }
                            break;
                        case DTYPEDECORATION_URI:
                            break;
                        case DTYPEEMBEDDED_URI:
                            break;
                        case DTYPEFILE_URI:
                            break;
                        case DTYPENUMBER_URI:
                            conditions.push(Operator.EQUALS, Operator.NOT_EQUALS, Operator.GREATERTHAN, Operator.LESSTHAN);
                            break;
                        case DTYPESELECT_URI:
                            conditions.push(Operator.EQUALS, Operator.NOT_EQUALS, Operator.ONE_OF, Operator.NOT_ONE_OF);
                            break;
                        case DTYPETEXT_URI:
                            conditions.push(Operator.EQUALS, Operator.NOT_EQUALS, Operator.ONE_OF, Operator.NOT_ONE_OF);
                            break;
                    }
                }
                if (!this.conditionsByComponent.get(family.family)) {
                    this.conditionsByComponent.set(family.family, new Map<string, Operator[]>());
                }
                this.conditionsByComponent.get(family.family).set(component.uri, conditions);
            }
            this.families.push(family);
        }
    }

    private onChangeField(field: string) {
        this.operator = undefined;
        this.operand = undefined;
        this.currentField = this.fields.find(col => col.name === field);
        this.exportFields = [];
        if (this.currentField) {
            this.fieldType = this.currentField.type;
            this.onChangeFieldType(this.currentField, this.fieldType);
        } else {
            this.fieldType = undefined;
        }
    }

    private onChangeExportField(exportField: string) {
        this.operator = undefined;
        this.operand = undefined;
        this.currentExportField = this.selectMasterDType?.column.find(col => col.name === exportField);
        if (this.currentExportField) {
            this.fieldType = this.currentExportField.type;
            this.onChangeFieldType(this.currentExportField, this.fieldType, false);
            const family = this.families.find(fam => fam.component.some(comp => comp.uri === this.currentExportField.type));
            if (family) {
                const conditions: string[] = this.conditionsByComponent.get(family.family).get(this.currentExportField.type);
                this.operators = this.data.operatorsOptions.filter(op => conditions.includes(op.code) && !(this.commonConditions as string[]).includes(op.code));
            }

            if (this.currentFieldIsSelect() || this.currentFieldIsAutocomplete()) {
                this.chiplistControl.setValue(null);
            }
        } else {
            this.fieldType = this.currentField.type;
            this.onChangeFieldType(this.currentField, this.fieldType, false);
        }
    }

    private onChangeFieldType(field: DTypeCol, fieldType: string, modifyExport = true) {
        if (fieldType === TRIGGER_COLUMN_URI) {
            if (field.name === '_userId') {
                const conditions: string[] = [Operator.ONE_OF, Operator.NOT_ONE_OF];
                this.operators = this.data.operatorsOptions.filter(op => conditions.includes(op.code));
            } else if (field.name === '_planningAreaId') {
                const conditions: string[] = [Operator.EQUALS, Operator.NOT_EQUALS, Operator.ONE_OF, Operator.NOT_ONE_OF];
                this.operators = this.data.operatorsOptions.filter(op => conditions.includes(op.code));
                if (!this.planningAreaList) {
                    this.servicesService.getPlanningAreaList(this.data.locator.serviceId)
                        .subscribe(areas => this.planningAreaList = areas.sort((a, b) => this.localize.transform(a.name).localeCompare(this.localize.transform(b.name))));
                }
            } else if (field.name === '_taskTypeUri') {
                const conditions: string[] = [Operator.EQUALS, Operator.NOT_EQUALS, Operator.ONE_OF, Operator.NOT_ONE_OF];
                this.operators = this.data.operatorsOptions.filter(op => conditions.includes(op.code));
                if (!this.taskTypeMap) {
                    this.taskTypeService.retrieveTaskTypeListCollectionDN(this.data.locator).subscribe(([collection, partaker]) => {
                        this.taskTypeMap = new Map<string, NextTaskType[]>(); 
                        Object.entries(collection).map(entry => {
                            const location = this.taskTypeService.getSubtitleFromLocator(entry[0], partaker);
                            if (!this.taskTypeMap.has(location)) {
                                this.taskTypeMap.set(location, []);
                            }
                            this.taskTypeMap.get(location).push(...Object.values(entry[1]));
                        });
                    });
                }
            }
        } else if (fieldType === SELECTFROMURI_COLUMN_URI) {
            const selectField = field as SelectFromUri;
            if (!selectField.option) {
                selectField.option = (field as SelectFromUri).uri ? this.data.dtype.link?.[(field as SelectFromUri).uri] : [];
            }
            if (modifyExport && selectField.option?.length > 0 && selectField.option[0].export) {
                this.exportFields = Object.keys(selectField.option[0].export);
            }
            const family = this.families.find(fam => fam.component.some(comp => comp.uri === fieldType));
            if (family) {
                const conditions: string[] = this.conditionsByComponent.get(family.family).get(fieldType);
                this.operators = this.data.operatorsOptions.filter(op => conditions.includes(op.code));
            }
            this.chiplistControl.setValue(null);
        } else {
            const family = this.families.find(fam => fam.component.some(comp => comp.uri === fieldType));
            if (family) {
                const conditions: string[] = this.conditionsByComponent.get(family.family).get(fieldType);
                this.operators = this.data.operatorsOptions.filter(op => conditions.includes(op.code));
            }

            if (this.fieldIsSelect(fieldType) || this.fieldIsAutocomplete(fieldType)) {
                this.chiplistControl.setValue(null);
            }
        }
    }

    private onChangeOperator(operator: Operator) {
        if ([
            Operator.EMPTY, Operator.NOT_EMPTY,
            Operator.HIDDEN, Operator.NOT_HIDDEN,
            Operator.MANDATORY, Operator.NOT_MANDATORY,
            Operator.READONLY, Operator.NOT_READONLY
        ].includes(operator)) {
            this.formGroup.get('operand').clearValidators();
            this.operand = undefined;
        } else {
            this.formGroup.get('operand').addValidators(Validators.required);
            if (this.fieldType === this.checkboxUri) {
                this.operand = false;
            } else {
                this.operand = undefined;
            }
        }
    }

    private filterOptions(value: any): SelectOption[] | AutocompleteOption[] {
        if (typeof value === 'string') {
            const filterValue = value.toLowerCase();
            if (this.currentFieldIsSelect()) {
                const options = (this.currentField as SelectWithGivenOptions).option
                    ?? ((this.currentField as SelectFromUri).uri ? this.data.dtype.link?.[(this.currentField as SelectFromUri).uri] : []);
                return options.filter(option =>
                    this.removeAccents(this.localize.transform(option.value)).toLowerCase()
                        .includes(this.removeAccents(filterValue)));
            } else if (this.currentFieldIsAutocomplete()) {
                const options = (this.currentField as AutocompleteWithGivenOptions).option
                    ?? ((this.currentField as AutocompleteFromUri).uri ? this.data.dtype.link?.[(this.currentField as SelectFromUri).uri] : []);
                return options.filter(option =>
                    this.removeAccents(this.localize.transform(option.value)).toLowerCase()
                        .includes(this.removeAccents(filterValue)));
            }
        }
    }

    private removeAccents(str: string) {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    }

    private openConfirmDialog(message: string) {
        return this._fuseConfirmationService.open({
            title: this.translocoService.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.translocoService.translate('confirm')
                },
                cancel: {
                    label: this.translocoService.translate('cancel')
                }
            }
        });
    }
}
